import { RecentlyViewed } from '@theme/recently-viewed-products';

const CATALOG_CACHE_KEY = 'bw_product_id_handle_map';

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    this.#render();
  }

  async #render() {
    const currentId = this.dataset.currentProductId || '';
    const maxProducts = Number(this.dataset.maxProducts) || 6;
    const ids = RecentlyViewed.getProducts()
      .filter((id) => String(id) !== String(currentId))
      .slice(0, maxProducts);

    if (ids.length === 0) {
      this.dataset.hasRecentlyViewed = 'false';
      return;
    }

    const idToHandle = await this.#getIdToHandleMap();
    const handles = ids.map((id) => idToHandle[id]).filter(Boolean);

    if (handles.length === 0) {
      this.dataset.hasRecentlyViewed = 'false';
      return;
    }

    const results = await Promise.allSettled(handles.map((handle) => this.#fetchCard(handle)));
    const cards = results
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => result.value);

    if (cards.length === 0) {
      this.dataset.hasRecentlyViewed = 'false';
      return;
    }

    this.dataset.hasRecentlyViewed = 'true';
    this.#renderCarousel(cards);
  }

  /**
   * The native Recently Viewed tracker (shipped in the theme's own
   * snippets/scripts.liquid) stores numeric product IDs, but rendering a
   * card requires a handle-based URL. Build an id -> handle lookup once per
   * session from the store's public product catalog.
   * @returns {Promise<Record<string, string>>}
   */
  async #getIdToHandleMap() {
    const cached = sessionStorage.getItem(CATALOG_CACHE_KEY);
    if (cached) {
      try {
        return JSON.parse(cached);
      } catch {
        // fall through and refetch
      }
    }

    /** @type {Record<string, string>} */
    const map = {};
    try {
      const response = await fetch('/products.json?limit=250');
      if (response.ok) {
        const data = await response.json();
        for (const product of data.products || []) {
          map[String(product.id)] = product.handle;
        }
      }
      sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(map));
    } catch {
      // leave map empty; caller treats missing entries as unresolved
    }
    return map;
  }

  /**
   * Fetches a single product rendered as a real theme product card via the
   * Sections Everywhere API, so it matches the Recommended Products block exactly.
   * @param {string} handle
   * @returns {Promise<string|null>}
   */
  async #fetchCard(handle) {
    try {
      const response = await fetch(`/products/${handle}?sections=recently-viewed-card`);
      if (!response.ok) return null;
      const data = await response.json();
      const html = data['recently-viewed-card'];
      if (!html) return null;
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      const item = wrapper.querySelector('.resource-list__item');
      return item ? item.innerHTML : null;
    } catch {
      return null;
    }
  }

  /**
   * Wraps fetched cards in the same slideshow-component markup the theme's
   * own resource-list-carousel snippet produces, so behavior matches exactly.
   * @param {string[]} cardHtmlList
   */
  #renderCarousel(cardHtmlList) {
    const target = this.querySelector('[data-recently-viewed-list]');
    if (!target) return;

    const timelineNames = cardHtmlList.map((_, index) => `--slide-${index + 1}`).join(', ');

    const slides = cardHtmlList
      .map(
        (html, index) => `
          <slideshow-slide
            ref="slides[]"
            aria-hidden="${index === 0 ? 'false' : 'true'}"
            style="view-timeline-name: --slide-${index}; --product-media-fit: cover;"
            class="resource-list__slide"
          >
            <div class="resource-list__item">${html}</div>
          </slideshow-slide>
        `
      )
      .join('');

    target.innerHTML = `
      <div
        class="resource-list__carousel"
        style="--gutter-slide-width: var(--util-page-margin-offset); --slide-width-max: 300px;"
      >
        <slideshow-component
          ref="resourceListCarousel"
          class="resource-list__carousel"
          style="--slideshow-timeline: ${timelineNames};"
          initial-slide="0"
        >
          <slideshow-container ref="slideshowContainer">
            <slideshow-arrows position="center">
              <button
                class="slideshow-control slideshow-control--previous slideshow-control--style-arrow button button-unstyled button-unstyled--transparent flip-x"
                aria-label="Previous slide"
                on:click="/previous"
                ref="previous"
              >
                <span class="svg-wrapper icon-arrow"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
              </button>
              <button
                class="slideshow-control slideshow-control--next slideshow-control--style-arrow button button-unstyled button-unstyled--transparent"
                aria-label="Next slide"
                on:click="/next"
                ref="next"
              >
                <span class="svg-wrapper icon-arrow"><svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
              </button>
            </slideshow-arrows>
            <slideshow-slides tabindex="-1" ref="scroller" gutters="start end">
              ${slides}
            </slideshow-slides>
          </slideshow-container>
        </slideshow-component>
      </div>
    `;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
